import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { getPageMetaInfo } from "@/utils/pageMetaInfo";
import { getCanonicalUrl } from "@/utils/canonicalUrls";

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  noindex?: boolean;
  structuredData?: object;
}

export const SEO = ({
  title,
  description,
  canonical,
  noindex = false,
  structuredData
}: SEOProps) => {
  const location = useLocation();
  const baseUrl = 'https://www.omnseotools.ch';
  
  // Get canonical synchronously - no state needed
  const currentCanonical = canonical || getCanonicalUrl(location.pathname);
  console.log('Current canonical URL:', currentCanonical);
  
  const pageMetaInfo = getPageMetaInfo(location.pathname);
  
  const finalTitle = title || pageMetaInfo.title || "OMN SEO Tools - Professionelle SEO-Tools für Ihren Online-Erfolg";
  const finalDescription = description || pageMetaInfo.description || "Entdecken Sie OMN SEO Tools, Ihre Plattform für effektive SEO-Strategien und nachhaltige Steigerung von Sichtbarkeit und Traffic.";

  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "OMN SEO Tools",
    "url": baseUrl,
    "description": finalDescription,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${baseUrl}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      <html lang="de" />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={currentCanonical} />
      
      <meta 
        name="robots" 
        content={noindex ? "noindex,nofollow" : "index,follow"} 
      />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={currentCanonical} />
      <meta property="og:image" content={`${baseUrl}/og-image.png`} />
      <meta property="og:locale" content="de_CH" />
      <meta property="og:site_name" content="OMN SEO Tools" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={`${baseUrl}/og-image.png`} />

      <meta name="author" content="OMN SEO Tools" />
      
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
    </Helmet>
  );
};